import React, { useEffect, useMemo, useRef, useState } from "react";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { Field, FieldProps, FormikErrors, FormikProps } from "formik";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { FormSelect } from "@whatslly/ui-components/v2/FormElements/FormSelect";
import { FormCheckBox } from "@whatslly/ui-components/v2/FormElements/FormCheckBox";
import { FormPasswordTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput/FormPasswordTextInput";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import styles from "./Modals.module.scss";
import { useDependencyInjector, useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { DestinationsStore } from "../DestinationsStore";
import { DestinationsModalButtons } from "../DestinationsModalButtons";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { DESTINATIONS_FILENAME_TEMPLATES } from "@whatslly/commons/destinationsFileNameTemplates";
import { TextInputWithCopy } from "@whatslly/ui-components/v2/TextInputWithCopy";
import { CheckBox } from "@whatslly/ui-components/v2/BaseElements/CheckBox";
import type { DestinationFormProps, FormDestinationDataType } from "../utils/DestinationsListTypes";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";

async function generateDek(): Promise<string> {
  const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
  const raw = new Uint8Array(await crypto.subtle.exportKey("raw", key));
  return btoa(String.fromCharCode(...raw));
}

const FILENAME_TEMPLATE = '{{orgId}}/{{sourcePhone}}/{{date timestamp "YYYY/MM/DD"}}.';
const FILENAME_TEMPLATE_OPTIONS = DESTINATIONS_FILENAME_TEMPLATES.map((template) => ({ label: template, value: template }));

type FormDataType = FormDestinationDataType<"r2"> & {
  dekEnabled?: boolean;
};

const BASE_DATA = {
  type: "r2" as const,
  integrationType: DESTINATION_INTEGRATION_TYPES.MESSAGES,
  settings: {
    format: "csv" as const,
    bucket: "",
    accountId: "",
    filenameTemplate: `${FILENAME_TEMPLATE}csv`,
    mediaFilenameTemplate: "{{orgId}}/{{sourcePhone}}/media/{{_id}}.{{fileType}}",
    dek: "",
  },
};

export const R2ModalForm: React.FC<DestinationFormProps<"r2">> = observer(({ onSubmitForm, settings }) => {
  const { t } = useTranslation();
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const formikContext = useRef<FormikProps<FormDataType>>(null);
  const [formatValue, setFormatValue] = useState<"json" | "csv">(settings?.format ?? "csv");
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const isSuperAdmin = useMemo(
    () => userStore.user?.isSuperAdmin || userStore.user?.isAdmin,
    [userStore.user?.isAdmin, userStore.user?.isSuperAdmin]
  );
  const [filenameTemplateSelect, setFilenameTemplateSelect] = useState<{ label: string; value: string } | undefined>();

  const validate = async (values?: FormDataType) => {
    const errors: FormikErrors<Partial<FormDataType>> = {};
    if (!values?.accountId) errors.accountId = t("field_is_required");
    if (!values?.format) errors.format = t("field_is_required");
    if (!values?.bucket) errors.bucket = t("field_is_required");
    if (!values?.filenameTemplate) errors.filenameTemplate = t("field_is_required");

    setFormikErrorHasErrors(Object.keys(errors).length > 0);
    return errors;
  };

  const onChangeFormat = (value: "json" | "csv") => {
    setFormatValue(value);
    const currentFileNameTemplateValue = formikContext.current?.values.filenameTemplate || `${FILENAME_TEMPLATE}.${value}`;
    const [currentValue] = currentFileNameTemplateValue.split(".");
    const newFileNameTemplateValue = FILENAME_TEMPLATE_OPTIONS.find((opt) => {
      const [optValue, optFormat] = opt.value.split(".");
      return optValue === currentValue && optFormat === value;
    }) || {
      label: `${FILENAME_TEMPLATE}.${value}`,
      value: `${FILENAME_TEMPLATE}.${value}`,
    };
    setFilenameTemplateSelect(newFileNameTemplateValue);
    formikContext.current?.setFieldValue("filenameTemplate", newFileNameTemplateValue?.value || `${FILENAME_TEMPLATE}.${value}`);
  };

  const onFileNameTemplateChange = (value: string) => {
    const mediaTemplate = value?.startsWith("{{sourcePhone}}")
      ? "{{sourcePhone}}/media/{{_id}}.{{fileType}}"
      : "{{orgId}}/{{sourcePhone}}/media/{{_id}}.{{fileType}}";
    formikContext.current?.setFieldValue("mediaFilenameTemplate", mediaTemplate);
  };

  const getFormValues = () => {
    const data = (formikContext.current?.values ?? {}) as FormDataType;
    delete data.dekEnabled;
    return { ...BASE_DATA, settings: { ...data, format: formatValue } };
  };

  const onSubmit = async () => {
    store.testConnection(t, getFormValues());
    const res = await store.testConnectionPromise;
    if (res?.success) {
      onSubmitForm(getFormValues());
    }
  };

  useEffect(() => {
    validate(getFormValues().settings);
  }, []);

  return (
    <FormElement initialValues={settings || BASE_DATA.settings} validate={validate} onSubmit={onSubmit} formikRef={formikContext}>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.format")} required />
        <FormSelect<{ value: "json" | "csv" }>
          name="format"
          paddingInline="none"
          options={[
            { label: "JSON", value: "json" },
            { label: "CSV", value: "csv" },
          ]}
          onChange={(selectedOption: { value: "json" | "csv" }) => onChangeFormat(selectedOption?.value)}
          defaultValue={
            settings?.format
              ? {
                  label: settings.format.toUpperCase(),
                  value: settings.format,
                }
              : { label: "CSV", value: "csv" }
          }
          isClearable={false}
          required
        />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.bucket")} required />
        <FormTextInput name="bucket" paddingInline="none" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text="Account ID" required />
        <FormTextInput name="accountId" paddingInline="none" placeholder="Your Cloudflare Account ID" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.accesses_key_id")} />
        <FormTextInput name="accessKeyId" paddingInline="none" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.secret_access_key")} />
        <FormPasswordTextInput name="secretAccessKey" paddingInline="none" />
      </div>
      <div className={classNames(styles.field, { [styles.hidden]: !isSuperAdmin })}>
        <Label className={styles.filedLabel} text={t("destinations_page.file_name_template")} required />
        <Select
          options={FILENAME_TEMPLATE_OPTIONS.filter((opt) => {
            return opt.value.includes(formatValue ?? "csv");
          })}
          value={filenameTemplateSelect}
          paddingInline="none"
          onChange={(selectedOption: (typeof FILENAME_TEMPLATE_OPTIONS)[number]) => {
            formikContext.current?.setFieldValue("filenameTemplate", selectedOption?.value);
            onFileNameTemplateChange(selectedOption?.value);
          }}
          defaultValue={
            FILENAME_TEMPLATE_OPTIONS.find((opt) =>
              settings?.filenameTemplate ? settings.filenameTemplate === opt.value : opt.value.includes(formatValue ?? "csv")
            ) ?? {
              label: `${FILENAME_TEMPLATE}.csv`,
              value: `${FILENAME_TEMPLATE}.csv`,
            }
          }
        />
        <div className={classNames(styles.field, styles.hidden)}>
          <FormTextInput name="filenameTemplate" paddingInline="none" />
        </div>
      </div>
      <div className={classNames(styles.field, styles.fieldCheckbox)}>
        <Label className={styles.filedLabel} text={t("destinations_page.store_media")} />
        <FormCheckBox name="storeMedia" paddingInline="none" />
      </div>
      <div className={classNames(styles.field, styles.fieldCheckbox)}>
        <Label className={styles.filedLabel} text={t("destinations_page.client_encryption_key")} />
        <Field
          name={"dekEnabled"}
          children={(fieldData: FieldProps<string, FormDataType>) => {
            return (
              <div>
                <CheckBox
                  disabled={Boolean(settings)}
                  name="dekEnabled"
                  paddingInline="none"
                  checked={Boolean(fieldData.form?.values["dek"])}
                  onChange={async (e) => {
                    if (!e.target.checked) {
                      formikContext.current?.setFieldValue("dek", "");
                      return;
                    }

                    const dek = await generateDek();
                    formikContext.current?.setFieldValue("dek", dek);
                    return;
                  }}
                />
              </div>
            );
          }}
        ></Field>
      </div>
      {settings ? null : (
        <Field
          name={`dek`}
          children={(fieldData: FieldProps<string, FormDataType>) => {
            return (
              fieldData.form?.values["dek"] && (
                <div className={styles.field}>
                  <Label className={styles.filedLabel} text={t("destinations_page.client_encryption")} />
                  <TextInputWithCopy type="password" value={fieldData.form?.values["dek"]} disabled={true} />
                </div>
              )
            );
          }}
        />
      )}
      <div className={classNames(styles.field, { [styles.hidden]: !isSuperAdmin })}>
        <Label className={styles.filedLabel} text={t("destinations_page.media_file_name_template")} />
        <FormTextInput name="mediaFilenameTemplate" paddingInline="none" disabled />
      </div>
      <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={formikErrorHasErrors} store={store} />
    </FormElement>
  );
});
